import { Box, Stack, Typography } from "@mui/material";
import { PoPanel, poColors } from "./poUi";

const toNumber = (value) => {
	const n = Number(value);
	return Number.isFinite(n) ? n : 0;
};

const money = (value) => toNumber(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const qtyText = (value) => toNumber(value).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const activeItems = (items = []) => (items || []).filter((row) => !row?.is_deleted);

/** Ginesys charge master is not in the replica. Name is keyed by CHGCODE. */
const PO_CHARGE_NAMES = {};

const chargeLabel = (row) => {
	const code = row.charge_code ?? row.chargeCode;
	const named = code != null && code !== "" ? PO_CHARGE_NAMES[Number(code)] : null;
	if (named) return named;
	const component = String(row.gst_component ?? row.gstComponent ?? "").trim();
	if (component) return component;
	return code != null && code !== "" ? `Charge ${code}` : "Charge";
};

const isPercentBasis = (basis) => ["P", "%", "PER", "PERCENT", "PERCENTAGE"].includes(String(basis || "").toUpperCase());
const isAmountBasis = (basis) => ["A", "AMT", "AMOUNT", "F", "RS", "R"].includes(String(basis || "").toUpperCase());

const chargeDetail = (row) => {
	const factor = row.charge_factor ?? row.chargeFactor;
	const applicable = row.applicable_amount ?? row.applicableAmount;
	const basis = row.charge_basis ?? row.chargeBasis;
	const itemCount = Number(row.item_count) || 0;
	const itemNote = itemCount > 1 ? `${itemCount} items` : "";
	if (row.mixed_factor) return itemNote;
	if (factor == null || factor === "") return itemNote;
	const hasApplicable = applicable != null && applicable !== "";
	let detail = "";
	if (isPercentBasis(basis) || (!basis && hasApplicable)) {
		detail = hasApplicable ? `${factor}% on ${money(applicable)}` : `${factor}%`;
	} else if (isAmountBasis(basis)) {
		detail = `${money(factor)} Rs`;
	} else if (Number(factor) !== 0) {
		detail = `${factor}`;
	}
	return [detail, itemNote].filter(Boolean).join(" · ");
};

const byChargeSeq = (a, b) => {
	const seqA = Number(a.charge_seq ?? a.chargeSeq);
	const seqB = Number(b.charge_seq ?? b.chargeSeq);
	if (Number.isFinite(seqA) && Number.isFinite(seqB) && seqA !== seqB) return seqA - seqB;
	return Number(a.charge_code ?? a.chargeCode) - Number(b.charge_code ?? b.chargeCode);
};

const rawChargeAmount = (row) => row?.charge_amount ?? row?.chargeAmount;

/** Header charge total can be non-zero while a charge row is still 0. Only show a line that has an amount. */
const isCalculatedCharge = (row) => {
	const amount = rawChargeAmount(row);
	if (amount == null || amount === "") return false;
	const n = Number(amount);
	return Number.isFinite(n) && n !== 0;
};

const isLessSign = (row) => {
	const sign = String(row?.charge_sign ?? row?.chargeSign ?? "ADD").toUpperCase();
	return sign === "LESS" || sign === "-" || sign === "L";
};

const signedValue = (row) => {
	const n = Number(rawChargeAmount(row));
	if (!Number.isFinite(n)) return 0;
	return isLessSign(row) ? -Math.abs(n) : n;
};

const signedAmount = (row) => {
	if (!isCalculatedCharge(row)) return null;
	const amount = rawChargeAmount(row);
	if (isLessSign(row)) return `(-) ${money(amount)}`;
	return money(amount);
};

/** Item charges live one row per line. The document row for the same code is often 0. */
const rollupLineCharges = (rows, activeItemIds) => {
	const grouped = new Map();
	for (const row of rows || []) {
		const itemId = row.purchase_order_item_id ?? row.purchaseOrderItemId;
		if (itemId != null && activeItemIds.size && !activeItemIds.has(String(itemId))) continue;
		if (!isCalculatedCharge(row)) continue;
		const code = row.charge_code ?? row.chargeCode;
		const key = String(code);
		const applicable = Number(row.applicable_amount ?? row.applicableAmount);
		const factor = String(row.charge_factor ?? row.chargeFactor ?? "");
		const current = grouped.get(key) || {
			charge_code: code,
			charge_seq: row.charge_seq ?? row.chargeSeq,
			charge_basis: row.charge_basis ?? row.chargeBasis,
			gst_component: row.gst_component ?? row.gstComponent,
			charge_factor: row.charge_factor ?? row.chargeFactor,
			net: 0,
			applicable: 0,
			hasApplicable: false,
			item_count: 0,
			factors: new Set(),
		};
		current.net += signedValue(row);
		if (Number.isFinite(applicable)) {
			current.applicable += applicable;
			current.hasApplicable = true;
		}
		current.item_count += 1;
		current.factors.add(factor);
		grouped.set(key, current);
	}
	return [...grouped.values()].map((row) => ({
		charge_code: row.charge_code,
		charge_seq: row.charge_seq,
		charge_basis: row.charge_basis,
		gst_component: row.gst_component,
		charge_factor: row.factors.size === 1 ? row.charge_factor : null,
		mixed_factor: row.factors.size > 1,
		charge_sign: row.net < 0 ? "LESS" : "ADD",
		charge_amount: Math.abs(row.net),
		applicable_amount: row.hasApplicable ? row.applicable : null,
		item_count: row.item_count,
	}));
};

const storedOrPending = (value) => (hasAmount(value) ? money(value) : "Not calculated");

const SummaryRow = ({ label, detail, amount, strong = false, pending = false }) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns: "minmax(140px, 1fr) minmax(160px, auto) 140px",
			gap: 1.5,
			alignItems: "baseline",
			py: strong ? 0.7 : 0.35,
			borderTop: strong ? `1px solid ${poColors.line}` : "none",
		}}>
		<Typography sx={{ fontSize: strong ? 13 : 12.5, fontWeight: strong ? 700 : 600, color: poColors.ink }}>{label}</Typography>
		<Typography sx={{ fontSize: 12, color: poColors.info, textAlign: "right" }}>{detail}</Typography>
		<Typography
			sx={{
				fontSize: strong ? 13 : 12.5,
				fontWeight: strong ? 700 : 600,
				color: pending ? poColors.warn : poColors.ink,
				textAlign: "right",
			}}>
			{amount}
		</Typography>
	</Box>
);

const hasAmount = (value) => value != null && value !== "";

const CostStateNote = ({ tone, title, message }) => (
	<Box
		sx={{
			mx: 1.75,
			my: 1.25,
			px: 1.5,
			py: 1.25,
			borderRadius: "10px",
			border: `1px solid ${tone.border}`,
			bgcolor: tone.bg,
		}}>
		<Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tone.color }}>{title}</Typography>
		<Typography sx={{ mt: 0.35, fontSize: 12.5, fontWeight: 500, color: poColors.ink }}>{message}</Typography>
	</Box>
);

const PoCostSummary = ({ form }) => {
	const items = activeItems(form?.items);
	const documentCharges = (form?.document_charges || []).filter(isCalculatedCharge);
	const documentCodes = new Set(documentCharges.map((row) => String(row.charge_code ?? row.chargeCode)));
	const activeItemIds = new Set(items.map((row) => String(row.id)));
	const lineCharges = rollupLineCharges(form?.line_charges, activeItemIds).filter(
		(row) => !documentCodes.has(String(row.charge_code)),
	);
	const calculatedCharges = [...documentCharges, ...lineCharges].sort(byChargeSeq);
	const basicValue = items.reduce((sum, row) => sum + toNumber(row.qty) * toNumber(row.rate), 0);
	const totalQty = items.reduce((sum, row) => sum + toNumber(row.qty), 0);
	const hasStoredGross = hasAmount(form?.gross_amount);
	const hasStoredNet = hasAmount(form?.net_amount);
	const hasStoredCharge = hasAmount(form?.charge_amount);
	const ginesysReady = form?.status === "PUBLISHED" && form?.ginesys_sync === "DONE" && !!form?.ginesys_po_id;

	return (
		<PoPanel title="Cost" contentSx={{ px: 0, py: 0 }}>
			<Stack
				direction="row"
				spacing={3}
				flexWrap="wrap"
				useFlexGap
				sx={{
					px: 1.75,
					py: 1,
					borderBottom: `1px solid ${poColors.line}`,
					bgcolor: poColors.panelBg,
				}}>
				<Typography sx={{ fontSize: 12.5, color: poColors.muted }}>
					Record Count:{" "}
					<Box component="span" sx={{ color: poColors.ink, fontWeight: 700 }}>
						{items.length}
					</Box>
				</Typography>
				<Typography sx={{ fontSize: 12.5, color: poColors.muted }}>
					Total Item Quantity:{" "}
					<Box component="span" sx={{ color: poColors.ink, fontWeight: 700 }}>
						{qtyText(totalQty)}
					</Box>
				</Typography>
				<Typography sx={{ fontSize: 12.5, color: poColors.muted }}>
					Basic Value:{" "}
					<Box component="span" sx={{ color: poColors.ink, fontWeight: 700 }}>
						{money(basicValue)}
					</Box>
				</Typography>
			</Stack>
			{calculatedCharges.length === 0 &&
				(ginesysReady || hasStoredCharge ? (
					<CostStateNote
						tone={{ color: poColors.warn, bg: poColors.warnBg, border: "#f3e0b5" }}
						title="Charge lines not loaded"
						message="Charge total is stored on the purchase order. Document and line charge amounts are still empty. Use Sync cost to load the Ginesys line amounts."
					/>
				) : (
					<CostStateNote
						tone={{ color: poColors.info, bg: poColors.infoBg, border: "#c5ddf8" }}
						title="Cost not available"
						message="Publish this purchase order and finish Ginesys sync before Ginesys can calculate these charges."
					/>
				))}
			<Box sx={{ px: 1.75, py: 1.25, ml: "auto", maxWidth: 720 }}>
				<SummaryRow label="Basic Value" detail="Qty × rate" amount={money(basicValue)} />
				{calculatedCharges.map((row, index) => (
					<SummaryRow
						key={row.id ?? `${row.charge_code}-${index}`}
						label={chargeLabel(row)}
						detail={chargeDetail(row)}
						amount={signedAmount(row)}
					/>
				))}
				<SummaryRow label="Gross Amount" amount={storedOrPending(form?.gross_amount)} pending={!hasStoredGross} strong />
				<SummaryRow label="Charge total" amount={storedOrPending(form?.charge_amount)} pending={!hasStoredCharge} />
				<SummaryRow label="Net Amount" amount={storedOrPending(form?.net_amount)} pending={!hasStoredNet} strong />
			</Box>
		</PoPanel>
	);
};

export default PoCostSummary;
